<template>
  <div class="flex flex-col w-full h-72 bg-white border border-gray-200 rounded-xl overflow-hidden">
    <div class="px-4 py-3 border-b border-gray-200">
      <h3 class="text-base font-medium mb-0" :class="theme.titleClass">{{ theme.title }}</h3>
    </div>

    <!-- Loading -->
    <div v-if="isLoading" class="relative h-50 flex-1 bg-white">
      <div class="absolute top-1/2 left-1/2 transform -translate-x-1/2 -translate-y-1/2 flex flex-col items-center">
        <svg class="h-26 w-16 animate-spin text-blue-600 mb-4" xmlns="http://www.w3.org/2000/svg" fill="none"
          viewBox="0 0 24 24">
          <circle class="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" stroke-width="2"></circle>
          <path d="M12 2 A 10 10 0 0 1 22 12" stroke="currentColor" stroke-width="2" stroke-linecap="round"></path>
        </svg>
      </div>
    </div>

    <div v-else class="grid grid-cols-2 flex-1">
      <!-- Peringkat 1 -->
      <div
        class="relative overflow-hidden flex flex-col items-center text-center px-4 py-6"
        :class="{ 'justify-center': !topItem }"
      >
        <span class="absolute -left-10 -bottom-12 w-40 h-40 rounded-full" :class="theme.circleClass"></span>
        <span class="absolute left-16 -bottom-20 w-36 h-36 rounded-full opacity-50" :class="theme.softCircleClass"></span>

        <!-- Data kosong -->
        <div v-if="!topItem" class="relative z-10 flex flex-col items-center gap-2">
          <ChartMixedDollarFilled v-if="variant === 'purchase'" className="w-16 h-16 text-gray-400" />
          <i v-else :class="theme.icon" class="text-5xl text-gray-400"></i>
          <p class="text-sm font-semibold text-gray-500 mb-0">No Data Available</p>
        </div>

        <div v-else class="relative z-10 flex flex-col items-center w-full min-w-0">
          <p class="flex items-center text-base font-semibold mb-0" :class="theme.metricClass">
            <ChartMixedDollarFilled v-if="variant === 'purchase'" className="w-4 h-4 mr-1 text-current" />
            <i v-else :class="theme.icon" class="mr-1"></i>
            {{ formatNumber(topItem.total) }} {{ theme.unit }}
          </p>
          <p class="text-xs text-gray-500 mb-0">
            from a total of {{ formatNumber(total) }} {{ theme.totalUnit }}
          </p>

          <div class="relative mt-4 mb-2">
            <span class="absolute -left-8 bottom-0 text-7xl font-bold leading-none text-gray-300 select-none">1</span>
            <img
              :src="coverUrl(topItem.image)"
              :alt="topItem.name"
              class="relative h-20 w-14 object-cover rounded"
              @error="onImageError"
            />
          </div>

          <p class="w-full text-xs text-gray-500 truncate mb-0" :title="topItem.author">
            {{ topItem.author || '-' }}
          </p>
          <p class="w-full text-sm font-medium text-gray-800 truncate mb-0" :title="topItem.name">
            {{ topItem.name || '-' }}
          </p>
        </div>
      </div>

      <!-- Peringkat 2 - 5 -->
      <ul class="border-l border-gray-100 px-4 py-1 mb-0">
        <li
          v-for="rank in otherRanks"
          :key="rank"
          class="flex items-start gap-3 py-2.5"
          :class="{ 'border-b border-gray-100': rank < lastRank }"
        >
          <span class="w-4 text-sm text-gray-500 pt-0.5">{{ rank }}</span>
          <div class="flex-1 min-w-0">
            <p class="text-sm text-gray-800 truncate mb-0" :title="itemAt(rank) ? itemAt(rank).name : ''">
              {{ itemAt(rank) ? itemAt(rank).name || '-' : 'No Data Available' }}
            </p>
            <p class="text-xs text-gray-500 mb-0">
              {{ itemAt(rank) ? `${formatNumber(itemAt(rank).total)} ${theme.unit}` : '-' }}
            </p>
          </div>
        </li>
      </ul>
    </div>
  </div>
</template>

<script>
import defaultCover from '@/assets/img/default.jpg';

const EBOOK_COVER_BASE_URL = 'https://ebooks.gramedia.com/ebook-covers/';
import ChartMixedDollarFilled from '@/components/icons/ChartMixedDollarFilled';
const THEMES = {
  purchase: {
    title: 'Most Purchase Content',
    icon: 'fa-solid fa-arrow-trend-up',
    unit: 'Purchases',
    totalUnit: 'purchases',
    titleClass: 'text-orange-800',
    metricClass: 'text-amber-700',
    circleClass: 'bg-orange-50',
    softCircleClass: 'bg-orange-100',
  },
  read: {
    title: 'Most Read Content',
    icon: 'fa-solid fa-book',
    unit: 'Reads',
    totalUnit: 'Reads',
    titleClass: 'text-green-700',
    metricClass: 'text-green-700',
    circleClass: 'bg-green-50',
    softCircleClass: 'bg-green-100',
  },
};

const LAST_RANK = 5;

export default {
  name: 'MostContentCard',
  components: {
    ChartMixedDollarFilled,
  },
  props: {
    variant: {
      type: String,
      default: 'purchase',
      validator: (value) => Object.keys(THEMES).includes(value),
    },
    total: {
      type: Number,
      default: 0,
    },
    items: {
      type: Array,
      default: () => [],
    },
    isLoading: {
      type: Boolean,
      default: false,
    },
  },
  data() {
    return {
      defaultCover,
      lastRank: LAST_RANK,
      otherRanks: [2, 3, 4, 5],
    };
  },
  computed: {
    theme() {
      return THEMES[this.variant];
    },
    topItem() {
      return this.itemAt(1);
    },
  },
  methods: {
    itemAt(rank) {
      return this.items[rank - 1] || null;
    },
    formatNumber(value) {
      return Number(value || 0).toLocaleString('id-ID');
    },
    coverUrl(image) {
      if (!image) return defaultCover;
      if (/^https?:\/\//i.test(image)) return image;

      const path = image.replace(/^\/+/, '');
      const withoutBucket = path.startsWith('images') ? path.substring(9) : path;
      return `${EBOOK_COVER_BASE_URL}${withoutBucket}`;
    },
    onImageError(event) {
      const img = event.target;
      if (img.dataset.fallback) return;
      img.dataset.fallback = 'true';
      img.src = defaultCover;
    },
  },
};
</script>
